/**
 * Pure reconciler: declarations + address + stored values in; URL/storage
 * writes out. No React, no router. `stored` is keyed by declaration id, then
 * param name.
 *
 * Evaluate every time; write only on load, activation, late registration,
 * deactivation, or a pathname change while the gate stays true. A search-param
 * edit that transitions no gate must not refill — that is what keeps Back
 * within a screen from bouncing the address forward.
 */

import type { PersistedSearchParamsDeclaration } from "./createPersistedSearchParams.types";

export type StoredByDeclaration = Record<string, Record<string, string | null>>;

export type ReconcileInput = {
  declarations: readonly PersistedSearchParamsDeclaration[];
  previousActiveIds: readonly string[];
  /** `undefined` on the first evaluation (load). Distinguishes navigation from a search-param edit. */
  previousPathname: string | undefined;
  pathname: string;
  params: URLSearchParams;
  stored: StoredByDeclaration;
};

export type ReconcileResult = {
  toSet: Record<string, string>;
  toRemove: string[];
  toStore: StoredByDeclaration;
  activeIds: string[];
};

const urlValue = (params: URLSearchParams, name: string): string | null =>
  params.has(name) ? params.get(name) : null;

const patchStore = (
  toStore: StoredByDeclaration,
  id: string,
  param: string,
  value: string | null
): void => {
  toStore[id] = { ...toStore[id], [param]: value };
};

export const reconcile = ({
  declarations,
  previousActiveIds,
  previousPathname,
  pathname,
  params,
  stored,
}: ReconcileInput): ReconcileResult => {
  const location = { pathname, params };
  const activeIds = declarations
    .filter((declaration) => declaration.activeWhen(location))
    .map((declaration) => declaration.id);
  const previousActive = new Set(previousActiveIds);
  const active = new Set(activeIds);
  const isLoad = previousPathname === undefined;
  const pathnameChanged =
    previousPathname !== undefined && previousPathname !== pathname;

  const shouldReconcile = (id: string): boolean =>
    active.has(id) && (isLoad || !previousActive.has(id) || pathnameChanged);

  const claimedByActive = new Set(
    declarations
      .filter((declaration) => active.has(declaration.id))
      .flatMap((declaration) => declaration.params.map((entry) => entry.param))
  );

  const toSet: Record<string, string> = {};
  const toRemove: string[] = [];
  const toStore: StoredByDeclaration = {};
  const byId = new Map(
    declarations.map((declaration) => [declaration.id, declaration])
  );

  for (const declaration of declarations) {
    if (!shouldReconcile(declaration.id)) continue;
    const slice = stored[declaration.id] ?? {};

    for (const entry of declaration.params) {
      const url = urlValue(params, entry.param);
      const remembered = slice[entry.param] ?? null;

      if (entry.mergeOnHydrate) {
        const effective = entry.mergeOnHydrate(url, remembered);
        if (effective !== url) {
          if (effective === null) {
            if (url !== null) toRemove.push(entry.param);
          } else {
            toSet[entry.param] = effective;
          }
        }
        if (effective !== remembered) {
          patchStore(toStore, declaration.id, entry.param, effective);
        }
        continue;
      }

      // Never resurrect a stored null: absent URL + null memory is a no-op.
      if (url === null) {
        if (remembered !== null) toSet[entry.param] = remembered;
        continue;
      }
      if (url !== remembered) {
        patchStore(toStore, declaration.id, entry.param, url);
      }
    }
  }

  // Deactivation: strip this declaration's params. Skip names another
  // still-active declaration claims — otherwise leaving A would delete B's param.
  for (const id of previousActiveIds) {
    if (active.has(id)) continue;
    const declaration = byId.get(id);
    if (!declaration) continue;
    for (const entry of declaration.params) {
      if (claimedByActive.has(entry.param)) continue;
      toRemove.push(entry.param);
    }
  }

  return {
    toSet,
    toRemove,
    toStore,
    activeIds,
  };
};

/**
 * True when this pass would change neither the URL nor localStorage.
 *
 * Why it exists: after we write the URL, React re-runs this logic because the
 * address changed. If that second pass also wrote, we'd loop (URL changes →
 * run again → URL changes again). Tests use this to prove a write settles in
 * one pass. Storage is part of the check so a merge that never matches what
 * we already stored cannot keep rewriting forever either.
 */
export const isReconcileNoop = (result: ReconcileResult): boolean =>
  Object.keys(result.toSet).length === 0 &&
  result.toRemove.length === 0 &&
  Object.keys(result.toStore).length === 0;
